import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import DatePicker from 'react-datepicker';
import api from '../../../utils/api';
import 'react-datepicker/dist/react-datepicker.css';
import './JobSeekers.css';
import '../CleanerDashboard/AvailableTasks.css';

const JobSeekers = () => {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const [jobSeekers, setJobSeekers] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedJobSeeker, setSelectedJobSeeker] = useState(null);
  const [modalDetailsLoading, setModalDetailsLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchJobSeekers();
  }, [startDate, endDate, searchQuery]);

  const fetchJobSeekers = async () => {
    try {
      const params = {};
      
      // Add search filter if provided
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      // Note: Date filtering would need to be implemented on the backend
      // For now, we'll fetch all and filter on frontend if needed
      
      const response = await api.get('/jobseekers', { params });
      let seekers = response.data.data;
      
      // Apply date filter on frontend if dates are selected
      if (startDate && endDate) {
        seekers = seekers.filter(seeker => {
          const seekerDate = new Date(seeker.createdAt);
          return seekerDate >= startDate && seekerDate <= endDate;
        });
      }
      
      // Sort: pending first, then by date
      const sorted = seekers.sort((a, b) => {
        if ((a.status || 'pending') === 'pending' && (b.status || 'pending') !== 'pending') return -1;
        if ((a.status || 'pending') !== 'pending' && (b.status || 'pending') === 'pending') return 1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
      setJobSeekers(sorted);
    } catch (error) {
      console.error('Error fetching job seekers:', error);
      showToast(error.response?.data?.message || t('admin.errorFetchingJobSeekers', 'Error fetching job seekers. Please try again.'), 'error');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleApprove = async (jobSeekerId) => {
    try {
      const response = await api.post(`/jobseekers/${jobSeekerId}/approve`);
      
      showToast(t('admin.jobSeekerApproved', 'Job seeker approved and converted to cleaner!'), 'success');
      fetchJobSeekers();
      setSelectedJobSeeker(null);
    } catch (error) {
      console.error('Error approving job seeker:', error);
      const errorMessage = error.response?.data?.message || t('admin.errorApproving', 'Error approving job seeker. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  const handleReject = async (jobSeekerId) => {
    if (!window.confirm(t('admin.confirmReject', 'Are you sure you want to reject this application?'))) {
      return;
    }

    try {
      await api.post(`/jobseekers/${jobSeekerId}/reject`);
      
      showToast(t('admin.applicationRejected', 'Application rejected.'), 'success');
      fetchJobSeekers();
      setSelectedJobSeeker(null);
    } catch (error) {
      console.error('Error rejecting application:', error);
      const errorMessage = error.response?.data?.message || t('admin.errorRejecting', 'Error rejecting application. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  const handleDelete = async (jobSeekerId) => {
    if (!window.confirm(t('admin.confirmDeleteJobSeeker', 'Are you sure you want to permanently delete this application?'))) {
      return;
    }

    try {
      await api.delete(`/jobseekers/${jobSeekerId}`);
      showToast(t('admin.jobSeekerDeleted', 'Application deleted successfully.'), 'success');
      fetchJobSeekers();
      setSelectedJobSeeker(null);
    } catch (error) {
      console.error('Error deleting job seeker:', error);
      const errorMessage = error.response?.data?.message || t('admin.errorDeletingJobSeeker', 'Error deleting application. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  const handleDownloadCV = async (e) => {
    e.preventDefault();
    const id = selectedJobSeeker?.id;
    if (!id) return;
    try {
      const response = await api.get(`/jobseekers/${id}/download/cv`, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (selectedJobSeeker.cv || 'cv').split('/').pop() || 'cv.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      if (err.response?.status === 302 && err.response?.headers?.location) {
        window.open(err.response.headers.location, '_blank');
      } else {
        showToast(err.response?.data?.message || t('admin.downloadError', 'Download failed.'), 'error');
      }
    }
  };

  const handleDownloadDocument = async (e, index) => {
    e.preventDefault();
    const id = selectedJobSeeker?.id;
    if (!id) return;
    try {
      const response = await api.get(`/jobseekers/${id}/download/document/${index}`, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const docKey = selectedJobSeeker.documents?.[index];
      a.download = (typeof docKey === 'string' ? docKey.split('/').pop() : null) || `document-${index + 1}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      if (err.response?.status === 302 && err.response?.headers?.location) {
        window.open(err.response.headers.location, '_blank');
      } else {
        showToast(err.response?.data?.message || t('admin.downloadError', 'Download failed.'), 'error');
      }
    }
  };

  const handleViewCVAndDetails = async (applicant) => {
    const id = applicant._id || applicant.id;
    if (!id) {
      setSelectedJobSeeker(applicant);
      return;
    }
    setModalDetailsLoading(true);
    setSelectedJobSeeker(applicant);
    try {
      const response = await api.get(`/jobseekers/${id}`);
      if (response.data?.data) {
        setSelectedJobSeeker(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching job seeker details:', err);
      showToast(err.response?.data?.message || t('admin.errorFetchingJobSeekerDetails', 'Could not load CV and documents.'), 'error');
    } finally {
      setModalDetailsLoading(false);
    }
  };

  if (initialLoading) {
    return <div className="loading">{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div className="job-seekers">
      <div className="job-seekers-header">
        <h2>{t('admin.jobSeekerApplications', 'Job Seeker Applications')}</h2>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="filter-group">
            <label>{t('common.search', 'Search')}:</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('admin.searchPlaceholder', 'Search by name, email, or phone...')}
              className="filter-select"
              style={{ minWidth: '250px' }}
            />
          </div>
          <div className="date-filter">
          <div className="date-filter-group">
            <label>{t('common.from', 'From')}:</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              placeholderText={t('common.startDate', 'Start date')}
              dateFormat="dd,MM,yyyy"
              className="date-picker-input"
            />
          </div>
          <div className="date-filter-group">
            <label>{t('common.to', 'To')}:</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              placeholderText={t('common.endDate', 'End date')}
              dateFormat="dd,MM,yyyy"
              className="date-picker-input"
            />
          </div>
            {(startDate || endDate || searchQuery) && (
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  setSearchQuery('');
                }}
              >
                {t('common.clearFilter', 'Clear Filter')}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {jobSeekers.length === 0 ? (
        <p className="no-applications">{t('admin.noApplications', 'No pending applications.')}</p>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="seekers-table">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('admin.name', 'Name')}</th>
                  <th>{t('auth.email', 'Email')}</th>
                  <th>{t('auth.phone', 'Phone')}</th>
                  <th>{t('admin.experience', 'Experience')}</th>
                  <th>{t('admin.appliedOn', 'Applied on')}</th>
                  <th>{t('admin.status', 'Status')}</th>
                  <th>{t('admin.action', 'Action')}</th>
                </tr>
              </thead>
              <tbody>
                {jobSeekers.map((applicant, index) => (
                  <tr key={applicant._id || applicant.id}>
                    <td>{index + 1}</td>
                    <td>{applicant.name}</td>
                    <td>{applicant.email}</td>
                    <td>{applicant.phone}</td>
                    <td>{applicant.experience} years</td>
                    <td>{new Date(applicant.createdAt).toLocaleDateString('en-GB')}</td>
                    <td>
                      <span className={`status status-${applicant.status || 'pending'}`}>
                        {applicant.status || 'pending'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleViewCVAndDetails(applicant)}
                        disabled={modalDetailsLoading}
                      >
                        {t('admin.details', 'Details')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="seekers-cards">
            {jobSeekers.map((applicant, index) => (
              <div key={applicant._id || applicant.id} className="seeker-card-mobile">
                <div className="task-card-mobile-header">
                  <h4>#{index + 1} {applicant.name}</h4>
                  <span className={`status status-${applicant.status || 'pending'}`}>
                    {applicant.status || 'pending'}
                  </span>
                </div>
                <div className="task-card-mobile-details">
                  <div className="task-card-mobile-row">
                    <span className="task-card-mobile-label">{t('auth.email', 'Email')}</span>
                    <span className="task-card-mobile-value">{applicant.email}</span>
                  </div>
                  <div className="task-card-mobile-row">
                    <span className="task-card-mobile-label">{t('auth.phone', 'Phone')}</span>
                    <span className="task-card-mobile-value">{applicant.phone}</span>
                  </div>
                  <div className="task-card-mobile-row">
                    <span className="task-card-mobile-label">{t('admin.experience', 'Experience')}</span>
                    <span className="task-card-mobile-value">{applicant.experience} years</span>
                  </div>
                  <div className="task-card-mobile-row">
                    <span className="task-card-mobile-label">{t('admin.appliedOn', 'Applied on')}</span>
                    <span className="task-card-mobile-value">{new Date(applicant.createdAt).toLocaleDateString('en-GB')}</span>
                  </div>
                </div>
                <div className="task-card-mobile-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleViewCVAndDetails(applicant)}
                    disabled={modalDetailsLoading}
                  >
                    {t('admin.details', 'Details')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {selectedJobSeeker && (
        <div className="modal-overlay" onClick={() => setSelectedJobSeeker(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('admin.jobSeekerDetails', 'Job Seeker Details')}</h3>
              <button className="close-btn" onClick={() => setSelectedJobSeeker(null)}>×</button>
            </div>
            <div className="modal-body">
              {modalDetailsLoading ? (
                <p className="loading-details">{t('common.loading', 'Loading...')}</p>
              ) : (
                <>
                  {/* Status Badge */}
                  <div className="at-detail-status">
                    <span className={`status status-${selectedJobSeeker.status || 'pending'}`} style={{ padding: '6px 18px', borderRadius: '20px', fontSize: '14px', fontWeight: 700, textTransform: 'capitalize' }}>
                      {selectedJobSeeker.status || 'pending'}
                    </span>
                  </div>

                  {/* Personal Information */}
                  <div className="at-detail-section">
                    <h4>{t('admin.personalInfo', 'Personal Information')}</h4>
                    <div className="at-detail-row">
                      <span className="at-detail-icon">👤</span>
                      <span><strong>{t('admin.name', 'Name')}:</strong> {selectedJobSeeker.name}</span>
                    </div>
                    <div className="at-detail-row">
                      <span className="at-detail-icon">📧</span>
                      <span><strong>{t('auth.email', 'Email')}:</strong> {selectedJobSeeker.email}</span>
                    </div>
                    <div className="at-detail-row">
                      <span className="at-detail-icon">📞</span>
                      <span><strong>{t('auth.phone', 'Phone')}:</strong> {selectedJobSeeker.phone}</span>
                    </div>
                    <div className="at-detail-row">
                      <span className="at-detail-icon">📍</span>
                      <span><strong>{t('auth.address', 'Address')}:</strong> {selectedJobSeeker.address || 'Not provided'}</span>
                    </div>
                    {(selectedJobSeeker.city || selectedJobSeeker.zipCode) && (
                      <div className="at-detail-row">
                        <span className="at-detail-icon">🏙️</span>
                        <span><strong>{t('auth.city', 'City')} / {t('auth.zipCode', 'Zip Code')}:</strong> {[selectedJobSeeker.city, selectedJobSeeker.zipCode].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                    <div className="at-detail-row">
                      <span className="at-detail-icon">⭐</span>
                      <span><strong>{t('admin.experience', 'Experience')}:</strong> {selectedJobSeeker.experience} years</span>
                    </div>
                    <div className="at-detail-row">
                      <span className="at-detail-icon">📅</span>
                      <span><strong>{t('admin.appliedOn', 'Applied on')}:</strong> {new Date(selectedJobSeeker.createdAt).toLocaleDateString('en-GB')}</span>
                    </div>
                  </div>

                  {/* About / Description */}
                  {selectedJobSeeker.description && (
                    <div className="at-detail-section">
                      <h4>{t('admin.about', 'About')}</h4>
                      <div className="at-detail-comments">{selectedJobSeeker.description}</div>
                    </div>
                  )}

                  {/* CV */}
                  <div className="at-detail-section">
                    <h4>{t('admin.cv', 'CV')}</h4>
                    {selectedJobSeeker.cv ? (
                      <>
                        <div className="at-detail-row">
                          <span className="at-detail-icon">📄</span>
                          <span><strong>{t('admin.cvFile', 'CV File')}:</strong> {typeof selectedJobSeeker.cv === 'string' ? selectedJobSeeker.cv.split('/').pop() : selectedJobSeeker.cv}</span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          style={{ marginTop: '8px' }}
                          onClick={handleDownloadCV}
                        >
                          {t('admin.downloadCV', 'Download CV')}
                        </button>
                      </>
                    ) : (
                      <p className="at-detail-sub">{t('admin.noCVUploaded', 'No CV uploaded.')}</p>
                    )}
                  </div>

                  {/* Additional Documents */}
                  <div className="at-detail-section">
                    <h4>{t('admin.additionalDocuments', 'Additional Documents')}</h4>
                    {selectedJobSeeker.documents && selectedJobSeeker.documents.length > 0 ? (
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {selectedJobSeeker.documents.map((doc, index) => (
                          <button
                            key={index}
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={(e) => handleDownloadDocument(e, index)}
                          >
                            📎 Document {index + 1}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="at-detail-sub">{t('admin.noDocumentsUploaded', 'No additional documents uploaded.')}</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="at-modal-actions" style={{ padding: '16px 0 0', borderTop: '1px solid #e2e8f0' }}>
                    {(!selectedJobSeeker.status || selectedJobSeeker.status === 'pending') && (
                      <>
                        <button
                          className="btn btn-success"
                          onClick={() => handleApprove(selectedJobSeeker._id || selectedJobSeeker.id)}
                        >
                          {t('admin.approveApplication', 'Approve Application')}
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleReject(selectedJobSeeker._id || selectedJobSeeker.id)}
                        >
                          {t('admin.rejectApplication', 'Reject Application')}
                        </button>
                      </>
                    )}
                    {selectedJobSeeker.status === 'rejected' && (
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(selectedJobSeeker._id || selectedJobSeeker.id)}
                      >
                        {t('admin.deleteApplication', 'Delete Application')}
                      </button>
                    )}
                    <button
                      className="btn btn-secondary"
                      onClick={() => setSelectedJobSeeker(null)}
                    >
                      {t('common.close', 'Close')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobSeekers;



